import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Briefcase, Users, Database, Building2, CalendarClock, CheckSquare,
  MessageSquare, FolderOpen, BarChart3, Sparkles, Settings as SettingsIcon, Search, Bell,
  Plus, HelpCircle, LogOut, PanelLeftClose, PanelLeftOpen, Menu, X, FileSignature, ShieldCheck,
  Workflow,
} from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/context/AuthContext";
import { api, apiError, initials, relativeTime } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { QuickAddDialog } from "@/components/forms/QuickAdd";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, mod: "dashboard" },
  { to: "/workflow", label: "Hiring Workflow", icon: Workflow, mod: "workflow" },
  { to: "/jobs", label: "Jobs", icon: Briefcase, mod: "jobs" },
  { to: "/candidates", label: "Candidates", icon: Users, mod: "candidates" },
  { to: "/talent-pool", label: "Talent Pool", icon: Database, mod: "talent_pool" },
  { to: "/clients", label: "Clients", icon: Building2, mod: "clients" },
  { to: "/interviews", label: "Interviews", icon: CalendarClock, mod: "interviews" },
  { to: "/tasks", label: "Tasks", icon: CheckSquare, mod: "tasks" },
  { to: "/communications", label: "Communications", icon: MessageSquare, mod: "communications" },
  { to: "/offers", label: "Offers & Joining", icon: FileSignature, mod: "offers" },
  { to: "/documents", label: "Documents", icon: FolderOpen, mod: "documents" },
  { to: "/analytics", label: "Reports & Analytics", icon: BarChart3, mod: "analytics" },
  { to: "/ai", label: "AI Intelligence", icon: Sparkles, mod: "ai" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, mod: null },
];

function visibleNav(can) {
  return NAV.filter((n) => !n.mod || can(n.mod, "view"));
}

const Brand = ({ collapsed, partner }) => (
  <Link to="/dashboard" className="flex items-center gap-2.5 px-4 py-5" data-testid="brand-logo">
    <span className="grid h-8 w-8 shrink-0 place-items-center rounded bg-blue-600 text-[13px] font-semibold text-white">
      D
    </span>
    {!collapsed && (
      <span className="min-w-0">
        <span className="block truncate text-[13px] font-semibold tracking-[0.16em] text-slate-900">
          {partner ? `DIGNIFYD × ${partner}` : "DIGNIFYD"}
        </span>
        <span className="block truncate text-[10px] tracking-[0.18em] text-slate-500">
          TALENT CONNECT
        </span>
      </span>
    )}
  </Link>
);

function SidebarNav({ items, collapsed, onNavigate }) {
  return (
    <nav className="flex-1 space-y-1 overflow-y-auto px-2 pb-4 thin-scroll">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onNavigate}
          data-testid={`nav-${item.to.replace("/", "") || "root"}`}
          className={({ isActive }) =>
            cn(
              "group relative flex items-center gap-3 rounded px-3 py-2.5 text-[13px] transition-colors duration-150",
              isActive
                ? "bg-blue-50 text-slate-900"
                : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            )
          }
        >
          {({ isActive }) => (
            <>
              {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-r bg-blue-500" />}
              <item.icon className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) {
      setRes(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setRes(data);
        setOpen(true);
      } catch {}
    }, 260);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => {
    setOpen(false);
    setQ("");
    navigate(path);
  };

  const groups = useMemo(() => {
    if (!res) return [];
    return [
      { key: "candidates", label: "Candidates", items: res.candidates, path: (i) => `/candidates/${i.id}`, main: (i) => i.full_name, sub: (i) => [i.current_designation, i.location].filter(Boolean).join(" · ") },
      { key: "jobs", label: "Jobs", items: res.jobs, path: (i) => `/jobs/${i.id}`, main: (i) => i.title, sub: (i) => [i.job_code, i.location].filter(Boolean).join(" · ") },
      { key: "clients", label: "Clients", items: res.clients, path: (i) => `/clients/${i.id}`, main: (i) => i.name, sub: (i) => i.industry },
      { key: "users", label: "Team", items: res.users, path: () => "/settings", main: (i) => i.name, sub: (i) => i.email },
    ].filter((g) => g.items && g.items.length);
  }, [res]);

  return (
    <Popover open={open && !!groups.length} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input
            data-testid="global-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search candidates, jobs, clients, skills…"
            className="h-9 border-slate-200 bg-white pl-9 text-sm text-slate-800 placeholder:text-slate-400 focus-visible:ring-blue-600"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[min(560px,92vw)] border-slate-200 bg-white p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
        data-testid="global-search-results"
      >
        <ScrollArea className="max-h-[60vh]">
          {res?.skills?.length ? (
            <div className="border-b border-slate-200 p-3">
              <p className="label-xs mb-2">Skills</p>
              <div className="flex flex-wrap gap-1.5">
                {res.skills.map((s) => (
                  <button
                    key={s}
                    onClick={() => go(`/talent-pool?skills=${encodeURIComponent(s)}`)}
                    className="rounded border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600 hover:border-blue-300"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {groups.map((g) => (
            <div key={g.key} className="border-b border-slate-200 p-2 last:border-0">
              <p className="label-xs px-2 py-1">{g.label}</p>
              {g.items.map((i) => (
                <button
                  key={i.id}
                  onClick={() => go(g.path(i))}
                  data-testid={`search-result-${g.key}`}
                  className="flex w-full items-center justify-between gap-3 rounded px-2 py-2 text-left hover:bg-slate-100"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-slate-800">{g.main(i)}</span>
                    <span className="block truncate text-xs text-slate-500">{g.sub(i)}</span>
                  </span>
                </button>
              ))}
            </div>
          ))}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

function Notifications() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = async () => {
    try {
      const res = await api.get("/notifications");
      setData(res.data);
    } catch {}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read", {});
    load();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-button"
          aria-label="Notifications"
          className="relative grid h-9 w-9 place-items-center rounded border border-slate-200 bg-white text-slate-500 transition-colors hover:text-slate-900"
        >
          <Bell className="h-4 w-4" />
          {data.unread > 0 && (
            <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-blue-600 px-1 text-[9px] font-medium text-white">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(380px,92vw)] border-slate-200 bg-white p-0">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <p className="text-sm font-medium text-slate-800">Notifications</p>
          <button onClick={markAll} data-testid="mark-all-read" className="text-[11px] text-blue-600 hover:text-blue-700">
            Mark all read
          </button>
        </div>
        <ScrollArea className="max-h-80">
          {data.items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-500">You are all caught up.</p>
          ) : (
            data.items.map((n) => (
              <div
                key={n.id}
                data-testid="notification-item"
                className={cn("border-b border-slate-200 px-4 py-3 last:border-0", !n.is_read && "bg-blue-50")}
              >
                <p className="text-sm text-slate-800">{n.title}</p>
                <p className="mt-0.5 text-xs text-slate-500">{n.body}</p>
                <p className="mt-1 text-[10px] text-slate-400">{relativeTime(n.created_at)}</p>
              </div>
            ))
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

function HelpDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-slate-200 bg-white">
        <DialogHeader>
          <DialogTitle className="text-slate-900">How recruiters use this platform</DialogTitle>
        </DialogHeader>
        <ol className="space-y-3 text-sm text-slate-500">
          {[
            "Search or import candidates, then upload a resume to auto-fill the profile.",
            "Open a Candidate 360 view to review the AI match breakdown before acting.",
            "Add the candidate to a job, then drag them across the pipeline board.",
            "Submit to client, schedule interviews and capture structured feedback.",
            "Raise the offer, track acceptance and confirm joining.",
          ].map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className="font-mono-num text-xs text-blue-600">0{i + 1}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
        <p className="flex items-center gap-2 rounded border border-slate-200 bg-slate-100 px-3 py-2 text-xs text-slate-500">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
          Client and candidate portal users only ever see their own records.
        </p>
      </DialogContent>
    </Dialog>
  );
}

export default function AppLayout({ children }) {
  const { user, logout, can } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [quickAdd, setQuickAdd] = useState(false);
  const [help, setHelp] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const items = useMemo(() => (user ? visibleNav(can) : []), [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const [partner, setPartner] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/settings").then((r) => setPartner(r.data?.partner_name || null)).catch(() => {});
  }, [user]);

  const canQuickAdd = can("candidates", "create") || can("jobs", "create");

  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside
        className={cn(
          "sticky top-0 hidden h-screen shrink-0 flex-col border-r border-slate-200 bg-white lg:flex",
          collapsed ? "w-[68px]" : "w-[248px]"
        )}
      >
        <Brand collapsed={collapsed} partner={partner} />
        <SidebarNav items={items} collapsed={collapsed} />
        <button
          onClick={() => setCollapsed((c) => !c)}
          data-testid="sidebar-collapse-toggle"
          aria-label="Toggle sidebar"
          className="flex items-center gap-3 border-t border-slate-200 px-4 py-3 text-xs text-slate-500 hover:text-slate-600"
        >
          {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          {!collapsed && <span>Collapse</span>}
        </button>
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[264px] border-slate-200 bg-white p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <Brand partner={partner} />
          <SidebarNav items={items} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="glass-bar sticky top-0 z-30 flex items-center gap-3 border-b border-slate-200 px-4 py-3 lg:px-6">
          <button
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-button"
            aria-label="Open navigation"
            className="grid h-9 w-9 shrink-0 place-items-center rounded border border-slate-200 text-slate-500 lg:hidden"
          >
            <Menu className="h-4 w-4" />
          </button>
          <div className="hidden flex-1 sm:block">
            <GlobalSearch />
          </div>
          <div className="flex flex-1 items-center justify-end gap-2 sm:flex-none">
            {canQuickAdd && (
              <Button
                onClick={() => setQuickAdd(true)}
                data-testid="quick-add-button"
                className="h-9 gap-1.5 rounded-full bg-blue-600 px-4 text-xs font-medium hover:bg-blue-500"
              >
                <Plus className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Quick Add</span>
              </Button>
            )}
            <Notifications />
            <button
              onClick={() => setHelp(true)}
              data-testid="help-button"
              aria-label="Help"
              className="hidden h-9 w-9 place-items-center rounded border border-slate-200 bg-white text-slate-500 hover:text-slate-900 sm:grid"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-button"
                  className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1 pl-1 pr-3 text-left hover:border-slate-300"
                >
                  <span className="grid h-7 w-7 place-items-center rounded-full bg-blue-100 text-[11px] font-medium text-blue-700">
                    {initials(user?.name)}
                  </span>
                  <span className="hidden min-w-0 sm:block">
                    <span className="block max-w-[130px] truncate text-xs text-slate-800">{user?.name}</span>
                    <span className="block max-w-[130px] truncate text-[10px] text-slate-500">
                      {user?.role_label}
                    </span>
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 border-slate-200 bg-white">
                <DropdownMenuLabel className="text-xs text-slate-500">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-slate-200" />
                <DropdownMenuItem onClick={() => navigate("/settings")} className="text-slate-600" data-testid="menu-settings">
                  <SettingsIcon className="mr-2 h-3.5 w-3.5" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setHelp(true)} className="text-slate-600">
                  <HelpCircle className="mr-2 h-3.5 w-3.5" /> Product guide
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-200" />
                <DropdownMenuItem
                  onClick={async () => {
                    await logout();
                    toast.success("Signed out");
                    navigate("/login");
                  }}
                  data-testid="logout-button"
                  className="text-rose-700"
                >
                  <LogOut className="mr-2 h-3.5 w-3.5" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <div className="border-b border-slate-200 px-4 py-2 sm:hidden">
          <GlobalSearch />
        </div>

        <main className="min-w-0 flex-1 px-4 py-6 lg:px-8 lg:py-8">
          <div key={location.pathname} className="fade-up mx-auto w-full max-w-[1600px]">
            {children}
          </div>
        </main>
      </div>

      <QuickAddDialog open={quickAdd} onOpenChange={setQuickAdd} />
      <HelpDialog open={help} onOpenChange={setHelp} />
    </div>
  );
}
